import classNames from "classnames";
import Dropdown from "rc-dropdown";
import "rc-dropdown/assets/index.css";
import Menu, { Divider, MenuItem, SubMenu } from "rc-menu";
import React, { useState } from "react";
import useSlidingIndicator, { INDICATOR_KEY_ATTRIBUTE } from "../../../shared/hooks/useSlidingIndicator";
import KuikaBadge from "../kuika-badge/kuika-badge";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import {
  KuikaPopupContainerGetter,
  useKuikaPopupContainer
} from "../kuika-popup-container/kuika-popup-container-context";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSmallBoldText from "../typography/SmallBoldText/SmallBoldText";
import KuikaText from "../typography/Text/Text";
import "./kuika-dropdown-menu.scss";

// The placements the antd 4.16 Dropdown underneath actually knows (rc-dropdown's builtin table).
// Any other string — "top" and "bottom" were once accepted here — resolves to no alignment, and the
// menu opens nowhere: the trigger looks dead. Keep this list in step with rc-dropdown.
export const KUIKA_DROPDOWN_MENU_PLACEMENTS = [
  "topLeft",
  "topCenter",
  "topRight",
  "bottomLeft",
  "bottomCenter",
  "bottomRight"
] as const;

export type KuikaDropdownMenuPlacement = (typeof KUIKA_DROPDOWN_MENU_PLACEMENTS)[number];

/** rc-dropdown's `transitionName`; kuika-dropdown-menu.scss animates the classes rc-motion derives from it. */
export const KUIKA_DROPDOWN_MOTION: string = "kuika-dropdown-motion";

// rc-menu renders the menu's children a second time, inside a `display: none` registration wrapper,
// so every element of the body exists twice. Refs must hold the copy the user sees.
const isInMenuMeasureCopy = (element: HTMLElement): boolean => {
  const hiddenWrapper: HTMLElement | null = element.closest<HTMLElement>('[aria-hidden="true"]');
  return hiddenWrapper !== null && hiddenWrapper.style.display === "none";
};

interface IKuikaDropdownMenuBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Filled in by rc-menu; meaningless on the body. */
  eventKey?: string;
  /** Filled in by rc-menu; meaningless on the body. */
  warnKey?: boolean;
}

// rc-menu clones every direct child of Menu with `eventKey` (and `warnKey` when it has no key) to
// register it as a row. The body — header, rows, footer — is Menu's only child and not a row, so
// those two props stop here instead of reaching the DOM.
const KuikaDropdownMenuBody = React.forwardRef<HTMLDivElement, IKuikaDropdownMenuBodyProps>(
  (
    { eventKey: _eventKey, warnKey: _warnKey, ...divProps }: IKuikaDropdownMenuBodyProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ): JSX.Element => <div ref={ref} {...divProps} />
);
KuikaDropdownMenuBody.displayName = "KuikaDropdownMenuBody";

export interface KuikaDropdownMenuItemType {
  key?: number | string;
  value?: string | number;
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  rightSlot?: string | React.ReactNode;
  group?: number | string;
  disabled?: boolean;
  children?: KuikaDropdownMenuItemType[];
  onClick?: (e: any) => void;
  danger?: boolean;
  showSelectedIcon?: boolean;
  hidden?: boolean;
  style?: React.CSSProperties;
  showDividerAfter?: boolean;
  // Keep the menu open after this item is clicked, overriding the menu's `closeOnSelect`.
  // For rows that change a setting you want to COMPARE rather than commit to — flipping
  // through appearance options is the case this exists for. Dismissing on each pick turns
  // "try these" into "reopen the menu ten times".
  keepOpenOnSelect?: boolean;
  // A row that hosts its own inline control (the account menu's theme row) and is not itself a
  // target to point at: no hover/focus highlight and no press fill. The control's own parts show
  // their states.
  noHighlight?: boolean;
  // Explicit test id for the rendered menu item. Needed when `label` is a
  // React node (the auto-generated testid stringifies to "[object Object]").
  dataTestId?: string;
}

interface KuikaDropdownMenuProps {
  children: ((open: boolean) => React.ReactNode) | React.ReactNode;
  options: KuikaDropdownMenuItemType[];
  open?: boolean;
  setOpen?: (open: boolean) => void;
  /**
   * The marked row. Pass it alone when the selection is owned elsewhere and the menu only
   * reports it; pass `setSelected` as well when the menu is also where it changes.
   */
  selected?: number | string | null;
  setSelected?: (key: number | string | null) => void;
  size?: "md" | "lg";
  overlayClassName?: string;
  header?: string | React.ReactNode;
  footer?: React.ReactNode;
  trigger?: "click" | "hover";
  placement?: KuikaDropdownMenuPlacement;
  offset?: [number, number];
  emptyState?: React.ReactNode;
  emptyStateCondition?: boolean;
  getPopupContainer?: (e?: any) => HTMLElement;
  closeOnSelect?: boolean;
  // Opt-in: dismiss when a scrollable ancestor of the trigger scrolls. rc-trigger aligns a popup
  // once and never follows an inner scroll container, so a menu opened from a row inside a
  // scrolling list is left hanging beside whatever row slid under it. Off by default — a docked
  // panel whose list scrolls under a menu is existing, accepted behaviour in several tools.
  // Scrolling inside this menu's own body never dismisses it.
  closeOnScroll?: boolean;
  showDividerAfter?: boolean;
  // Optional stable test-id applied to the popover's content container. Lets a
  // consumer expose its menu as a reliable scoping anchor (e.g. the workspace
  // switcher) without leaking an internal CSS class into test locators.
  menuTestId?: string;
  /**
   * Cap the popup at the trigger's own width. rc-dropdown only sets a MINIMUM width, so a long
   * option otherwise widens the menu until it hits the viewport and then wraps inside a row of
   * fixed height. With the cap the options clip and reveal on hover instead.
   */
  matchTriggerWidth?: boolean;
}

const KuikaDropdownMenu: React.FC<KuikaDropdownMenuProps> = ({
  children,
  options,
  open: controlledOpen,
  setOpen: controlledSetOpen,
  selected: controlledSelected,
  setSelected: controlledSetSelected,
  size = "md",
  overlayClassName,
  header,
  footer,
  trigger = "click",
  placement = "bottomLeft",
  offset = [0, 0],
  emptyState,
  emptyStateCondition,
  getPopupContainer,
  closeOnSelect = true,
  closeOnScroll = false,
  showDividerAfter = true,
  menuTestId,
  matchTriggerWidth = false
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  // An explicit prop wins; inside a KuikaDrawer the menu mounts in the drawer, not <body>.
  const popupContainerFromContext: KuikaPopupContainerGetter | undefined = useKuikaPopupContainer();
  const [internalSelected, setInternalSelected] = useState<number | string | null>(null);

  const isControlledOpen = controlledOpen !== undefined && controlledSetOpen !== undefined;
  // `selected` alone is enough to control the mark. The setter used to be required too, so a
  // caller whose selection is owned elsewhere — a role held on the server, the current
  // screen, anything the menu does not decide — passed `selected`, got no error, and
  // watched the menu ignore it and track its own internal pick instead.
  const isControlledSelected = controlledSelected !== undefined;

  const open = isControlledOpen ? controlledOpen : internalOpen;
  // `open` + `setOpen` = fully controlled. `setOpen` alone = uncontrolled with notification:
  // rc-dropdown keeps owning visibility while the consumer still hears every change. That
  // matters because rc-menu mounts its children a second time in a display:none registration
  // pass, so a controlled trigger inside a dropdown body exists twice and both copies share
  // one open flag — they then open together, and a re-click closes one twin on mousedown only
  // for the other to reopen on click. Per-instance state can't be shared, so it can't race.
  const setOpen = (next: boolean): void => {
    if (isControlledOpen) {
      controlledSetOpen!(next);
      return;
    }
    setInternalOpen(next);
    controlledSetOpen?.(next);
  };

  // Scroll fires on the element that scrolled and does not bubble, so listen on the capture
  // phase at the document. Close only when the scrolled node actually contains this trigger —
  // an unrelated list scrolling elsewhere is not our business — and never when the scroll came
  // from inside our own popup, which is how a long menu body stays open while you scroll it.
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  // Measured when the menu opens: the trigger can be resized (the left panel is draggable) and the
  // popup is only mounted from that moment on.
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
  // rc-menu's own Arrow/Home/End navigation walks real DOM focus between its rows — nothing
  // moves focus there when the popup opens via a click, so without this Arrow keys silently do
  // nothing (they only ever reach whatever still has focus, outside the popup, which is why
  // ArrowDown used to look like it "stuck" on the first row). Focused explicitly by DOM query
  // (not rc-menu's own `Menu.focus()` handle) so a row a stale hover left active internally never
  // steals reopen focus — the existing "reopens without the row the pointer last rested on" rule.
  // Skipped for trigger="hover": the WAI-ARIA menu-button pattern moves focus on activation, not
  // on a pointer merely passing over the trigger — grabbing it there would yank focus out of
  // whatever the user was doing (typing in an input, say) for a menu they never asked to interact
  // with, and hand it back to the trigger the moment the pointer moves away again.
  const isHoverTrigger: boolean = trigger === "hover";
  const openRef: React.MutableRefObject<boolean> = React.useRef<boolean>(open);
  openRef.current = open;
  const popupRef = React.useRef<HTMLDivElement | null>(null);
  // The checked row when the menu has one, else the first: a list longer than the menu's height
  // then opens scrolled to the current value (focus brings the row into view), and the arrow keys
  // start from it, as a native select's do.
  const focusOpeningRow = (): void => {
    // rc-menu writes `data-menu-id` from its own internal id context, which is not yet on the
    // DOM at the first commit (it settles a render later) — query by a class this component
    // controls from the start instead. `li`, because a selected sub-menu marks its title div too.
    const popup: HTMLDivElement | null = popupRef.current;
    const openingRow: HTMLElement | null =
      popup?.querySelector<HTMLElement>(
        "li.kuika-dropdown__menu-item--selected:not(.rc-dropdown-menu-item-disabled)"
      ) ??
      popup?.querySelector<HTMLElement>(".kuika-dropdown__menu-item:not(.rc-dropdown-menu-item-disabled)") ??
      null;
    openingRow?.focus();
  };
  const attachPopup = (element: HTMLDivElement | null): void => {
    if (element !== null && !isInMenuMeasureCopy(element)) {
      // rc-trigger can mount the popup on a LATER commit than `open` flipping true (a frame after
      // its trigger is in the DOM — see the drawer/select tests for the same lag), so this first
      // attach is the reliable point to focus, not just the effect below.
      const isFirstAttach: boolean = popupRef.current === null;
      popupRef.current = element;
      if (isFirstAttach && openRef.current && !isHoverTrigger) {
        focusOpeningRow();
      }
    }
  };
  React.useEffect((): void => {
    if (isHoverTrigger) {
      return;
    }
    if (open) {
      focusOpeningRow();
      return;
    }
    // Closing: a row may still hold focus (it stays in the DOM, just hidden, until the exit
    // motion ends) — hand it back to the trigger instead of letting it fall to <body>.
    const activeElement: Element | null = document.activeElement;
    const focusWasInPopup: boolean = activeElement !== null && (popupRef.current?.contains(activeElement) ?? false);
    if (focusWasInPopup) {
      const triggerFocusable: HTMLElement | null =
        triggerRef.current?.querySelector<HTMLElement>("button, [href], input, select, textarea, [tabindex]") ?? null;
      triggerFocusable?.focus();
    }
  }, [open, isHoverTrigger]);
  // A list that loads after the menu opened (a skeleton in `emptyState` until then) had no row to
  // land on when it did; land when the rows arrive. Not when focus is already somewhere in the
  // popup — someone typing in the header's search field keeps the field.
  const hasRows: boolean = options.length > 0;
  const hadRowsRef: React.MutableRefObject<boolean> = React.useRef<boolean>(hasRows);
  React.useEffect((): void => {
    const haveRowsArrived: boolean = hasRows && !hadRowsRef.current;
    hadRowsRef.current = hasRows;
    if (!haveRowsArrived || !open || isHoverTrigger) {
      return;
    }
    const activeElement: Element | null = document.activeElement;
    const isFocusInPopup: boolean = activeElement !== null && (popupRef.current?.contains(activeElement) ?? false);
    if (!isFocusInPopup) {
      focusOpeningRow();
    }
  }, [hasRows, open, isHoverTrigger]);
  // One highlight slides between the rows under the pointer or the keyboard focus, instead of each
  // row painting its own hover background.
  const scrollableRef: React.MutableRefObject<HTMLDivElement | null> = React.useRef<HTMLDivElement | null>(null);
  const attachScrollable = (element: HTMLDivElement | null): void => {
    if (element !== null && !isInMenuMeasureCopy(element)) {
      scrollableRef.current = element;
    }
  };
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const remeasureHighlight: () => void = useSlidingIndicator(scrollableRef, highlightKey);
  // Rows replaced under a hidden highlight (a header search filtering the list) give the hook nothing
  // to react to while the list stays at its height cap, so it is re-measured here — as the entity
  // picker does on its items.
  React.useLayoutEffect((): void => {
    if (open) {
      remeasureHighlight();
    }
  }, [open, options, remeasureHighlight]);
  React.useEffect(() => {
    if (!matchTriggerWidth || !open) return;
    setTriggerWidth(triggerRef.current?.getBoundingClientRect().width ?? null);
  }, [matchTriggerWidth, open]);
  React.useEffect(() => {
    if (!closeOnScroll || !open) return undefined;
    const onScrollCapture = (event: Event): void => {
      const scrolled: Node | null = event.target as Node | null;
      const trigger: HTMLDivElement | null = triggerRef.current;
      if (!scrolled || !trigger) return;
      const popup: HTMLDivElement | null = popupRef.current;
      if (popup?.contains(scrolled)) return;
      if (scrolled.contains(trigger)) {
        setOpen(false);
      }
    };
    document.addEventListener("scroll", onScrollCapture, true);
    return () => document.removeEventListener("scroll", onScrollCapture, true);
  }, [closeOnScroll, open]);

  // A menu reopens without the row the pointer last rested on. Reset while rendering the closed menu
  // (React's pattern for state that follows a prop), not in an effect after it painted.
  if (!open && highlightKey !== null) {
    setHighlightKey(null);
  }

  // Delegated from the visible body: the row under the pointer or holding keyboard focus (rc-menu
  // moves focus row by row on ↑/↓) gets the highlight; a disabled row takes it away.
  const trackHighlight = (event: React.SyntheticEvent<HTMLDivElement>): void => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const row: HTMLElement | null = event.target.closest<HTMLElement>(`[${INDICATOR_KEY_ATTRIBUTE}]`);

    // Headers and dividers keep the highlight where it is, so crossing them does not break a slide.
    // A sub-menu's rows sit in their own popup (its React portal still bubbles here); the
    // highlight stays on the row that opened it.
    if (row === null || !event.currentTarget.contains(row)) {
      return;
    }

    // A disabled row, or one that opted out of the highlight, takes it away rather than keeping it.
    const isUnhighlightable: boolean =
      row.closest('[aria-disabled="true"]') !== null ||
      row.classList.contains("kuika-dropdown__menu-item--no-highlight");
    setHighlightKey(isUnhighlightable ? null : row.getAttribute(INDICATOR_KEY_ATTRIBUTE));
  };

  const clearHighlight = (): void => {
    setHighlightKey(null);
  };

  const selected = isControlledSelected ? controlledSelected : internalSelected;
  const setSelected = (key: number | string | null) => {
    if (isControlledSelected) {
      // Read-only when no setter was given: the value comes back through `selected`.
      controlledSetSelected?.(key);
    } else {
      setInternalSelected(key);
    }
  };

  // useEffect(() => {
  //   const handleIframeClick = () => {
  //     if (open) {
  //       setOpen(false);
  //     }
  //   };

  //   const handleWindowBlur = () => {
  //     if (open) {
  //       setOpen(false);
  //     }
  //   };

  //   if (open) {
  //     const iframes = document.querySelectorAll("iframe");
  //     iframes.forEach((iframe) => {
  //       iframe.addEventListener("load", () => {
  //         try {
  //           const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
  //           if (iframeDocument) {
  //             iframeDocument.addEventListener("click", handleIframeClick);
  //           }
  //         } catch (e) {
  //           window.addEventListener("blur", handleWindowBlur);
  //         }
  //       });
  //     });

  //     window.addEventListener("blur", handleWindowBlur);
  //   }

  //   return () => {
  //     const iframes = document.querySelectorAll("iframe");
  //     iframes.forEach((iframe) => {
  //       const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
  //       if (iframeDocument) {
  //         iframeDocument.removeEventListener("click", handleIframeClick);
  //       }
  //     });
  //     window.removeEventListener("blur", handleWindowBlur);
  //   };
  // }, [open, setOpen]);

  const getItemKey = (item: KuikaDropdownMenuItemType): number | string => {
    return item.key !== undefined ? item.key : item.value!;
  };

  const handleSelect = (key: number | string) => {
    if (isControlledSelected) {
      controlledSetSelected?.(key);
    } else {
      setSelected(key);
    }
    if (closeOnSelect) {
      setOpen(false);
    }
  };

  // `rowKey` is the row's position ("3", or "2.1" inside a sub-menu). Options may carry neither `key`
  // nor `value`, and then every row's own key reads "undefined": the position is what keeps rows apart
  // for React and for the sliding highlight.
  // rc-menu keys a row by its `eventKey`, which it only fills in on Menu's direct children — and the
  // rows here sit inside the body. So each row is handed the same key React gets.
  const getRowMenuKey = (itemKey: number | string | undefined, rowKey: string): string =>
    itemKey === undefined ? `row-${rowKey}` : String(itemKey);

  const renderMenuItem = (item: KuikaDropdownMenuItemType, rowKey: string) => {
    if (item.hidden) {
      return null;
    }
    const itemKey = getItemKey(item);
    const rowMenuKey: string = getRowMenuKey(itemKey, rowKey);
    const menuItemClassName = classNames("kuika-dropdown__menu-item", OVERFLOW_REVEAL_HOST_CLASS, {
      "kuika-dropdown__menu-item--selected":
        selected === itemKey && (!item.onClick || item.showSelectedIcon) && item.showSelectedIcon !== false,
      "kuika-dropdown__menu-item--with-left-slot": item.icon,
      "kuika-dropdown__menu-item--danger": item.danger,
      "kuika-dropdown__menu-item--no-highlight": item.noHighlight
    });

    return (
      <MenuItem
        data-testid={
          item.dataTestId ??
          "dropdown" +
            item.label
              ?.toString()
              .replace(/-/g, "")
              .replace(/^[^.]*\./, "")
              .replace(/\s/g, "") +
            "Button"
        }
        data-onboarding-id={`ui-kit_dropdown-menu_${item.label}`.replace(/\s/g, "").toLowerCase()}
        key={rowMenuKey}
        eventKey={rowMenuKey}
        data-indicator-key={rowKey}
        className={menuItemClassName}
        disabled={item.disabled}
        style={item.style}
        onClick={
          item.onClick
            ? (e) => {
                if (item.onClick) {
                  item.onClick(e);
                }
                if (closeOnSelect && !item.keepOpenOnSelect) {
                  setOpen(false);
                }
              }
            : (e) => {
                handleSelect(itemKey);
              }
        }
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {selected === itemKey && (!item.onClick || item.showSelectedIcon) && item.showSelectedIcon !== false && (
          <KuikaSvg name="check" width={16} height={16} />
        )}
        {item.icon && <div className="kuika-dropdown__menu-item--left-slot">{item.icon}</div>}
        <KuikaText className="kuika-dropdown__menu-item--label">
          {typeof item.label === "string" ? <KuikaOverflowText>{item.label}</KuikaOverflowText> : item.label}
        </KuikaText>
        {(item.rightSlot || item.rightIcon) && (
          <div className="kuika-dropdown__menu-item--right-slot-wrapper">
            {item.rightSlot && <KuikaBadge content={item.rightSlot} />}
            {item.rightIcon}
          </div>
        )}
      </MenuItem>
    );
  };

  const renderSubMenu = (item: KuikaDropdownMenuItemType, rowKey: string) => {
    const itemKey = getItemKey(item);
    const rowMenuKey: string = getRowMenuKey(itemKey, rowKey);
    const subMenuItemClassName = classNames("kuika-dropdown__menu-item kuika-dropdown__sub-menu", {
      "kuika-dropdown__menu-item--selected": selected === itemKey,
      "kuika-dropdown__menu-item--with-left-slot": item.icon
    });

    const title = (
      <div className={subMenuItemClassName} data-indicator-key={rowKey}>
        <div className="kuika-dropdown__menu-item--left-slot">{item.icon}</div>
        {selected === itemKey && <KuikaSvg name="check" width={16} height={16} color="blue" />}
        {item.label}
        <div className="kuika-dropdown__menu-item--right-slot-wrapper">
          {item.rightSlot && <KuikaBadge content={item.rightSlot} />}
          {item.rightIcon}
        </div>
      </div>
    );

    return (
      <SubMenu
        key={rowMenuKey}
        eventKey={rowMenuKey}
        title={title}
        disabled={item.disabled}
        className="kuika-dropdown__sub-menu"
      >
        {item.children?.map((subItem: KuikaDropdownMenuItemType, subIndex: number): React.ReactNode =>
          renderMenuItem(subItem, `${rowKey}.${subIndex}`)
        )}
      </SubMenu>
    );
  };

  const renderMenuContent = () => {
    let currentGroup: number | string | null = null;
    const menuItems: React.ReactNode[] = [];

    options.forEach((item: any, index) => {
      if (item.group !== currentGroup) {
        if (typeof item.group === "string") {
          menuItems.push(
            <div key={`header-${item.group}`} className="kuika-dropdown__menu-group-header">
              {typeof item.group === "string" ? <KuikaSmallBoldText>{item.group}</KuikaSmallBoldText> : item.group}
            </div>
          );
        } else if (typeof item.group === "number" && index !== 0 && showDividerAfter) {
          menuItems.push(<Divider key={`separator-${item.group}`} className="kuika-dropdown__separator" />);
        }
        currentGroup = item.group;
      }

      if (item.children) {
        menuItems.push(renderSubMenu(item, String(index)));
      } else {
        menuItems.push(renderMenuItem(item, String(index)));
      }
    });

    return menuItems;
  };

  const renderMenuWithHeaderAndFooter = () => (
    <KuikaDropdownMenuBody
      className="kuika-dropdown__menu-container"
      ref={attachPopup}
      style={matchTriggerWidth && triggerWidth ? { maxWidth: triggerWidth } : undefined}
    >
      {header && (
        <div className="kuika-dropdown__menu-header">
          {typeof header === "string" ? <KuikaSmallBoldText>{header}</KuikaSmallBoldText> : header}
        </div>
      )}
      <div
        className="kuika-dropdown__menu-scrollable-content"
        ref={attachScrollable}
        onMouseOver={trackHighlight}
        onFocus={trackHighlight}
        onMouseLeave={clearHighlight}
      >
        <span className="kuika-sliding-indicator kuika-dropdown__menu-highlight" aria-hidden="true" />
        {renderMenuContent()}
        {emptyState && emptyStateCondition && <div className="kuika-dropdown__menu--empty-state">{emptyState}</div>}
      </div>
      {footer && <div className="kuika-dropdown__menu-footer">{footer}</div>}
    </KuikaDropdownMenuBody>
  );

  // `menuTestId` belongs on the Menu ROOT, not on a node inside it. antd's Menu renders its
  // children through rc-menu/rc-overflow, which mounts every child TWICE — once in the real
  // `ul.rc-dropdown-menu` and once in an offscreen 0x0 measurement wrapper. A test-id placed on
  // the container div therefore always resolves to two elements and every strict-mode locator
  // built on it fails (the four Left Panel Language scenarios died on exactly this). The Menu
  // root is rendered once, so the hook stays unique while still wrapping the same items.
  const menu = (
    <Menu
      data-testid={menuTestId}
      selectedKeys={selected !== null && selected !== undefined ? [String(selected)] : []}
      className={classNames("kuika-dropdown__menu-content", `kuika-dropdown__menu--${size}`, {
        "kuika-dropdown__menu--controlled-selected": isControlledSelected
      })}
    >
      {renderMenuWithHeaderAndFooter()}
    </Menu>
  );

  return (
    <Dropdown
      overlayClassName={classNames("kuika-dropdown__menu", overlayClassName)}
      transitionName={KUIKA_DROPDOWN_MOTION}
      trigger={trigger}
      overlay={menu}
      visible={open}
      onVisibleChange={setOpen}
      placement={placement}
      align={{
        targetOffset: [offset[0], offset[1]]
      }}
      getPopupContainer={getPopupContainer ?? popupContainerFromContext}
    >
      <div className="kuika-dropdown__menu--custom-trigger" ref={triggerRef}>
        {typeof children === "function" ? children(open) : children}
      </div>
    </Dropdown>
  );
};

export default KuikaDropdownMenu;
